import { useValidation } from '@xengage/gw-portals-validation-react';
import {
    useDataRefresh,
    ViewModelForm,
    ViewModelServiceContext
} from '@xengage/gw-portals-viewmodel-react';
import { useTranslator } from '@jutro/locale';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState, useContext } from 'react';
import appConfig from 'app-config';
import messages from './FNOLPolicySearch.messages';
import metadata from './FNOLPolicySearch.metadata.json5';
import styles from './FNOLPolicySearch.module.scss';

function FNOLPolicySearch(props) {
    const { isComponentValid, onValidate, registerComponentValidation } =
        useValidation('FNOLPolicySearch');
    const {
        policySearchCriteriaVM,
        setPolicySearchCriteriaVM,
        lossDate,
        isValidLossDate,
        handlePolicySearchByNumber,
        setPolicyData,
        policyData,
        handleSearchPolicyByDetails,
        isAPICallInProgress
    } = props;

    const { refreshData } = useDataRefresh();
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const [providePolicyNumber, setProvidePolicyNumber] = useState(lossDate ? 'providePolicyNumber' : "");
    const [searchForPolicy, setSearchForPolicy] = useState("");
    const [insuredLocOrPolicyMailingAddr, setInsuredLocOrPolicyMailingAddr] = useState("");

    

    const validateSearchCriteria = useCallback(() => {
        const policySearchCriteriaValue = _.get(
            policySearchCriteriaVM,
            'value'
        );
        return !_.isEmpty(policySearchCriteriaValue);
    }, [policySearchCriteriaVM]);

    useEffect(
        () => registerComponentValidation(validateSearchCriteria),
        [registerComponentValidation, validateSearchCriteria]
    );

    useEffect(() => {
        if (policyData?.length === 1) {
            _.set(policySearchCriteriaVM, 'policyNumber.value', policyData[0]?.policyNumber);
            setPolicySearchCriteriaVM(policySearchCriteriaVM);
        }
    }, [policyData]);

    const handleChange = useCallback(
        newModel => {
            setPolicySearchCriteriaVM(newModel);
            refreshData();
        },
        [setPolicySearchCriteriaVM, refreshData]
    );

    const handleSearchForPolicy = useCallback((value) => {
        setSearchForPolicy(() => value);
        setProvidePolicyNumber('');
        setInsuredLocOrPolicyMailingAddr('insuredLocation');
        if (policyData?.length > 0) {
            setPolicyData({});
        }
        _.set(policySearchCriteriaVM, 'policyNumber.value', null);
        _.set(policySearchCriteriaVM, 'useInsuredAddressExt.value', true);
        _.set(policySearchCriteriaVM, 'isAgentDetailSearchExt.value', true);
        setPolicySearchCriteriaVM(policySearchCriteriaVM);
    }, [policyData, policySearchCriteriaVM]);

    const handleProvidePolicyNumber = useCallback((value) => {
        setSearchForPolicy('');
        setProvidePolicyNumber(() => value);
        _.set(policySearchCriteriaVM, 'isAgentDetailSearchExt.value', false);
        handlePolicyNumber();
    }, [policySearchCriteriaVM, policyData]);

    const handleInsuredLocOrPolicyMailingAddr = useCallback((value) => {
        setInsuredLocOrPolicyMailingAddr(value);
       // const policySearchCriteriaVm = _.clone(policySearchCriteriaVM);
        if (value === 'insuredLocation') {
            _.set(policySearchCriteriaVM, 'useInsuredAddressExt.value', true);
        } else {
            _.set(policySearchCriteriaVM, 'useInsuredAddressExt.value', false);
        }
        setPolicySearchCriteriaVM(policySearchCriteriaVM);
    }, [policySearchCriteriaVM]);

    const resetPolicySearch = useCallback(() => {
        const policySearchCriteriaVm = _.clone(policySearchCriteriaVM);

        _.set(policySearchCriteriaVm, 'value', { policyNumber: policySearchCriteriaVM.value.policyNumber, 
            country: 'US' });


        const policySearchCriteriaViewModel = viewModelService.create(
            policySearchCriteriaVm.value,
            'cc',
            'edge.capabilities.claim.fnol.policy.dto.PolicySearchCriteriaDTO'
        );

        // _.set(policySearchCriteriaVm, 'value', { policyNumber: policySearchCriteriaVM.value.policyNumber });
        setPolicySearchCriteriaVM(policySearchCriteriaViewModel);
        refreshData();

        if (policyData?.length > 0) { 
            setPolicyData({});
        }
    }, [policySearchCriteriaVM, viewModelService, setPolicySearchCriteriaVM, refreshData, 
        policyData, setPolicyData]);

    const handlePolicyNumber = useCallback(() => {
        const policyNumber = _.get(policySearchCriteriaVM.value, 'policyNumber');
        const pc9PN = /^[A-Z]{4}[0-9]{10}$/;
        const pc7PN = /^[0-9]{9}$/;
        const pc7PlusTermPN = /^[0-9]{9}-[0-9]*$/;
        const isValidPolicyNumber = policyNumber?.match(pc9PN) || policyNumber?.match(pc7PN) || policyNumber?.match(pc7PlusTermPN);
        if (isValidPolicyNumber) {
            handlePolicySearchByNumber();
        }
        return isValidPolicyNumber;
    }, [policySearchCriteriaVM]);

    const handleSearchPolicy = useCallback(() => {
        handleSearchPolicyByDetails();
    }, [policySearchCriteriaVM]);

    const writeValue = useCallback((value, path) => {
        const policySearchCriteriaVm = _.clone(policySearchCriteriaVM);
        if(path === 'lastName' && value) {
            _.set(policySearchCriteriaVm, 'companyNameExt.value', '');
        } 
        if (path === 'companyNameExt' && value) {
            _.set(policySearchCriteriaVm, 'lastName.value', '');
        }
        setPolicySearchCriteriaVM(policySearchCriteriaVm);
        refreshData();
    }, [policySearchCriteriaVM, refreshData]);

    const overrides = {
        policyVerificationContainer: {
            visible: isValidLossDate && appConfig.persona !== 'policyholder'
        },
        searchPolicyField: {
            onValueChange: handleSearchForPolicy,
            value: searchForPolicy
        },
        providePolicyNumberField: {
            onValueChange: handleProvidePolicyNumber,
            value: providePolicyNumber
        },
        insuredLocOrPolicyMailingAddrField: {
            onValueChange: handleInsuredLocOrPolicyMailingAddr,
            value: insuredLocOrPolicyMailingAddr
        },
        streetNameOrAddress: {
            label: insuredLocOrPolicyMailingAddr === 'insuredLocation' ? translator(messages.streetName) : translator(messages.streetAddress)
        },
        streetNumber: {
            visible: insuredLocOrPolicyMailingAddr === 'insuredLocation'
        },
        nameAddressFieldsContainer: {
            visible: !!searchForPolicy && isValidLossDate
        },
        policyNumberInput: {
            disabled: !!searchForPolicy,
            onBlur: handlePolicyNumber
        },
        searchButton: {
            disabled: !isComponentValid || isAPICallInProgress
        },
        firstName: {
            visible: _.isEmpty(_.get(policySearchCriteriaVM, 'companyNameExt.value'))
        },
        lastName: {
            visible: _.isEmpty(_.get(policySearchCriteriaVM, 'companyNameExt.value'))
        },
        companyName: {
            visible: _.isEmpty(_.get(policySearchCriteriaVM, 'lastName.value'))
        }

    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            resetPolicySearch: resetPolicySearch,
            searchPolicyByDetails: handleSearchPolicy
        },
    };


    return ( 
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={policySearchCriteriaVM}
            overrideProps={overrides}
            onModelChange={handleChange}
            onValidationChange={onValidate}
            classNameMap={resolvers.resolveClassNameMap}
            callbackMap={resolvers.resolveCallbackMap}
            onValueChange={writeValue}
        />
    );
}

FNOLPolicySearch.propTypes = {
    policySearchCriteriaVM: PropTypes.shape({
        policyType: PropTypes.shape({}),
    }).isRequired
};

export default FNOLPolicySearch;
